import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Zap, Building2, CheckCircle, ArrowRight, Calendar, Loader2, Shield, Mail } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AuditTeaser } from "@/components/AuditTeaser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDAR_LINK = "https://calendar.app.google/gbZSzz4TiFAYYSjL8";

function authHeaders() {
  const t = localStorage.getItem("roas_session_token");
  return t ? { "Authorization": `Bearer ${t}` } : {};
}

export default function PricingDemoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupMode, setSignupMode] = useState("demo"); // "demo" or plan_id
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });
  const [signingUp, setSigningUp] = useState(false);

  const pollPaymentStatus = useCallback((sessionId) => {
    const poll = async (attempts) => {
      if (attempts >= 8) { setPollingSession(null); return; }
      setPollingSession(sessionId);
      try {
        const resp = await fetch(`${API}/subscriptions/status/${sessionId}`, { credentials: "include", headers: authHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          if (data.payment_status === "paid" || (data.payment_status === "no_payment_required" && data.status === "complete")) {
            toast.success("Welcome to ROAS-AI! Your 7-day free trial has started.");
            setPollingSession(null);
            setTimeout(() => navigate("/campaigns"), 1500);
            return;
          } else if (data.status === "expired") {
            toast.error("Payment session expired.");
            setPollingSession(null);
            return;
          }
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch { setPollingSession(null); }
    };
    poll(0);
  }, [navigate]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams, pollPaymentStatus]);

  // Google sign-in for demo (first-party OAuth)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleDemo = () => {
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}&intent=demo`;
  };

  // Google sign-in for subscription (first-party OAuth)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSubscribe = (planId) => {
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}&intent=subscribe&plan=${encodeURIComponent(planId)}`;
  };

  // Email signup then demo or subscribe
  const handleEmailSignup = async () => {
    if (!signupForm.name || !signupForm.email || !signupForm.password) {
      toast.error("All fields are required");
      return;
    }
    setSigningUp(true);
    try {
      const resp = await fetch(`${API}/auth/signup`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signupForm),
      });
      if (resp.ok) {
        if (signupMode === "demo") {
          // Create demo session with their real email
          const demoResp = await fetch(`${API}/auth/demo-session`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: signupForm.email, name: signupForm.name }),
          });
          if (demoResp.ok) {
            const demoData = await demoResp.json();
            if (demoData.token) localStorage.setItem("roas_session_token", demoData.token);
          }
          toast.success("Welcome! Launching demo...");
          navigate("/campaigns");
        } else {
          // Proceed to checkout
          setShowSignup(false);
          await doCheckout(signupMode);
        }
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Signup failed");
      }
    } catch {
      toast.error("Signup failed");
    } finally {
      setSigningUp(false);
    }
  };

  const doCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const resp = await fetch(`${API}/subscriptions/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      if (resp.ok) {
        const data = await resp.json();
        window.location.href = data.url;
      } else {
        toast.error("Failed to create checkout");
      }
    } catch {
      toast.error("Payment error");
    } finally {
      setLoadingPlan(null);
    }
  };

  const openSignup = (mode) => {
    setSignupMode(mode);
    setShowSignup(true);
  };

  const features = [
    "Cross-platform campaign sync (Google, Meta, LinkedIn, Bing)",
    "AI-powered Advisor with interactive charts",
    "Agentic AI Simulator — predict ROAS, audience reaction & budget outcomes before you spend",
    "Budget Groups with blended metric optimization",
    "Campaign hierarchy view (Campaign > Ad Group > Ad)",
    "Automated budget reallocation based on ROAS",
    "SOC2-compliant security & audit logging",
  ];

  document.title = "ROAS-AI Pricing return ( Demo | AI Ad Budget Optimizer";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/login" className="flex items-center gap-2">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-8 h-8 object-contain" />
            <span className="font-heading text-lg font-bold">ROAS-AI</span>
          </a>
          <a href="/login" className="text-zinc-400 hover:text-white text-sm transition-colors">Sign In</a>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 mb-4">See ROAS-AI in Action or Choose Your Plan</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Maximize Your Ad Performance<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">Across Every Platform</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Meet the Agentic AI Simulator — predict a campaign's ROAS, audience reaction & budget scenarios before you spend. Plus one dashboard for Google, Meta, LinkedIn & Bing Ads with AI optimization that delivers 25-40% ROAS lifts.
          </p>

          {/* Metrics + Simulator Highlight */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">40%</p>
              <p className="text-white text-xs font-semibold">Increase in ROAS</p>
            </div>
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl px-5 py-3 text-center">
              <p className="text-2xl font-bold text-violet-400">35%</p>
              <p className="text-white text-xs font-semibold">Reduction in CAC</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-3 text-center flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <div className="text-left">
                <p className="text-white text-sm font-semibold">Agentic AI Simulator</p>
                <p className="text-zinc-400 text-xs">Predict outcomes before you spend</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Demo */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="bg-[#121212] border border-white/10 rounded-2xl p-8 flex flex-col">
            <div className="flex-1">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-violet-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Explore the Demo</h2>
              <p className="text-zinc-400 mb-6">Experience full dashboard access with sample data. No credit card required.</p>
              <ul className="space-y-3 mb-6">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mb-8">
                <AuditTeaser />
              </div>
            </div>

            <div className="space-y-3">
              <Button
                data-testid="google-demo-btn"
                onClick={handleGoogleDemo}
                size="lg"
                className="w-full bg-white text-black hover:bg-zinc-200 font-semibold h-13 text-base rounded-xl"
              >
                <FcGoogle className="w-5 h-5 mr-2" />
                Continue with Google
              </Button>
              <Button
                data-testid="email-demo-btn"
                onClick={() => openSignup("demo")}
                size="lg"
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5 font-semibold h-13 text-base rounded-xl"
              >
                <Mail className="w-5 h-5 mr-2" />
                Sign Up with Email
              </Button>
            </div>
          </motion.div>

          {/* Right: Pricing */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-4">
            {/* Pilot - Limited Discount */}
            <div className="bg-[#121212] border-2 border-emerald-500/40 rounded-2xl p-6 relative">
              <Badge className="absolute -top-3 right-4 bg-emerald-600 text-white">56% OFF — Limited Time</Badge>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-bold">Pilot Plan</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">1 user license, starter access</p>
                </div>
                <div className="text-right">
                  <p className="text-zinc-400 text-sm line-through">$899/mo</p>
                  <p className="text-3xl font-bold text-emerald-400">$399</p>
                  <p className="text-zinc-400 text-sm">/month</p>
                </div>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={() => handleGoogleSubscribe("pilot_monthly")}
                  disabled={loadingPlan === "pilot_monthly"}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold h-11 rounded-xl"
                >
                  {loadingPlan === "pilot_monthly" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FcGoogle className="w-4 h-4 mr-2" />}
                  Subscribe with Google
                </Button>
                <Button
                  onClick={() => openSignup("pilot_monthly")}
                  variant="outline"
                  className="w-full border-white/10 text-zinc-300 hover:bg-white/5 h-10 rounded-xl text-sm"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Subscribe with Email
                </Button>
              </div>
            </div>

            {/* Monthly */}
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-violet-500/30 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-bold">Individual Monthly</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">1 user license, full platform access, 7-day free trial</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">$899</p>
                  <p className="text-zinc-400 text-sm">/month</p>
                </div>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={() => handleGoogleSubscribe("individual_monthly")}
                  disabled={loadingPlan === "individual_monthly"}
                  className="w-full bg-white text-black hover:bg-zinc-200 font-semibold h-11 rounded-xl"
                >
                  {loadingPlan === "individual_monthly" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FcGoogle className="w-4 h-4 mr-2" />}
                  Start 7-Day Free Trial with Google
                </Button>
                <Button
                  onClick={() => openSignup("individual_monthly")}
                  variant="outline"
                  className="w-full border-white/10 text-zinc-300 hover:bg-white/5 h-10 rounded-xl text-sm"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Start 7-Day Free Trial with Email
                </Button>
              </div>
            </div>

            {/* Annual */}
            <div className="bg-[#121212] border-2 border-violet-500/40 rounded-2xl p-6 relative">
              <Badge className="absolute -top-3 right-4 bg-violet-600 text-white">Save $798/yr</Badge>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-violet-400" />
                    <h3 className="text-lg font-bold">Individual Annual</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">1 user license, full platform access, 7-day free trial</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">$9,990</p>
                  <p className="text-zinc-400 text-sm">/year</p>
                </div>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={() => handleGoogleSubscribe("individual_annual")}
                  disabled={loadingPlan === "individual_annual"}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold h-11 rounded-xl"
                >
                  {loadingPlan === "individual_annual" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FcGoogle className="w-4 h-4 mr-2" />}
                  Start 7-Day Free Trial with Google
                </Button>
                <Button
                  onClick={() => openSignup("individual_annual")}
                  variant="outline"
                  className="w-full border-white/10 text-zinc-300 hover:bg-white/5 h-10 rounded-xl text-sm"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Start 7-Day Free Trial with Email
                </Button>
              </div>
            </div>

            {/* Enterprise */}
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-bold">Enterprise</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">Custom scale, volume & API keys</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-400">Custom</p>
                  <p className="text-zinc-400 text-sm">Billed annually</p>
                </div>
              </div>
              <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 font-semibold h-11 rounded-xl">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Discovery Call
                </Button>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Trust */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
          <p className="text-zinc-500 text-sm">Trusted by 50+ marketing teams worldwide</p>
          <div className="flex items-center justify-center gap-6 mt-4 text-zinc-500 text-xs">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> SOC2 Compliant</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> PCI Compliant via Stripe</span>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> OAuth 2.0 Secured</span>
          </div>
        </motion.div>

        <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex gap-4">
            <a href="/privacy" className="hover:text-zinc-400">Privacy Policy</a>
            <a href="/terms" className="hover:text-zinc-400">Terms of Service</a>
            <a href="/about" className="hover:text-zinc-400">About Us</a>
          </div>
          <span>Copyright (c) 2026 Beruf LLC</span>
        </div>
      </div>

      {/* Email Signup Dialog */}
      <Dialog open={showSignup} onOpenChange={setShowSignup}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {signupMode === "demo" ? "Sign Up to Access Demo" : "Sign Up to Subscribe"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Full Name</Label>
              <Input value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} placeholder="John Doe" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Email</Label>
              <Input type="email" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} placeholder="you@company.com" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Password</Label>
              <Input type="password" value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} placeholder="Min 8 chars, uppercase, lowercase, number" className="bg-black/50 border-white/10 text-white" />
            </div>
            <Button onClick={handleEmailSignup} disabled={signingUp} className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold h-12 rounded-xl">
              {signingUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {signupMode === "demo" ? "Sign Up & Launch Demo" : "Sign Up & Continue to Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment polling overlay */}
      {pollingSession && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 text-center max-w-md">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
            <h3 className="text-white text-lg font-semibold mb-2">Processing Payment</h3>
            <p className="text-zinc-400 text-sm">Verifying your payment status...</p>
          </div>
        </div>
      )}
    </div>
  );
}
